import React, { useState } from 'react';
import { Calculator } from 'lucide-react';
import { useApp } from '../../contexts/AppContext';
import { STADTGEBIETE, LAGE_OPTIONS, STELLPLATZ_OPTIONS, SCHWIERIGKEIT_OPTIONS } from '../../constants';
import { formatValue } from '../../utils';
import { validateValue, propertyValidationRules, sanitizeInput } from '../../utils/validators';

const InputSection: React.FC = () => {
  const { currentInputs, updateCurrentInput } = useApp();
  const [, setValidationErrors] = useState<{ [key: string]: string }>({});

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { id, name, type, value } = e.target;
    const key = id || name;
    
    let processedValue: any = value;
    
    if (type === 'checkbox') {
      processedValue = (e.target as HTMLInputElement).checked;
    } else if (type === 'number' || type === 'range') {
      processedValue = parseFloat(value) || 0;
      // Sanitize numeric inputs
      processedValue = sanitizeInput(processedValue, key);
    }
    
    // Validate the value
    const rule = propertyValidationRules[key];
    if (rule) {
      const error = validateValue(processedValue, rule);
      setValidationErrors(prev => ({
        ...prev,
        [key]: error || ''
      }));
    }
    
    updateCurrentInput(key, processedValue);
  };

  return (
    <div className="w-full bg-gray-50 rounded-2xl p-4 shadow-sm border border-gray-100 mb-8">
      <h2 className="font-bold text-gray-800 mb-6 flex items-center gap-2">
        <Calculator size={24} />
        Eingabeparameter
      </h2>
      
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Allgemeines */}
        <div className="p-3 bg-white rounded-lg shadow-sm border border-gray-100">
          <h4 className="font-semibold text-gray-800 mb-3">Allgemeines</h4>
          <div className="space-y-1">
            <div className="compact-input-row">
              <label htmlFor="propertyName" className="text-xs font-medium text-gray-600">Immobilienname:</label>
              <input
                type="text"
                id="propertyName"
                value={currentInputs.propertyName}
                onChange={handleInputChange}
                className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div className="compact-input-row">
              <label htmlFor="besichtigung" className="text-xs font-medium text-gray-600">Besichtigt:</label>
              <input
                type="date"
                id="besichtigung"
                value={currentInputs.besichtigung}
                onChange={handleInputChange}
                className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div className="compact-input-row">
              <label htmlFor="stadtgebiet" className="text-xs font-medium text-gray-600">Stadtgebiet:</label>
              <select
                id="stadtgebiet"
                value={currentInputs.stadtgebiet}
                onChange={handleInputChange}
                className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              >
                {STADTGEBIETE.map(gebiet => (
                  <option key={gebiet} value={gebiet}>{gebiet}</option>
                ))}
              </select>
            </div>
            
            <div className="compact-input-row">
              <label htmlFor="lage" className="text-xs font-medium text-gray-600">Lage:</label>
              <select
                id="lage"
                value={currentInputs.lage}
                onChange={handleInputChange}
                className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              >
                {LAGE_OPTIONS.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
            
            <div className="compact-input-row">
              <label htmlFor="baujahr" className="text-xs font-medium text-gray-600">Baujahr:</label>
              <input
                type="number"
                id="baujahr"
                value={currentInputs.baujahr}
                onChange={handleInputChange}
                className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div className="compact-input-row">
              <label htmlFor="wohnflaeche" className="text-xs font-medium text-gray-600">Wohnfläche (m²):</label>
              <input
                type="number"
                id="wohnflaeche"
                value={currentInputs.wohnflaeche}
                onChange={handleInputChange}
                className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div className="compact-input-row">
              <label htmlFor="zimmer" className="text-xs font-medium text-gray-600">Zimmer:</label>
              <input
                type="number"
                id="zimmer"
                value={currentInputs.zimmer}
                onChange={handleInputChange}
                className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div className="compact-input-row">
              <label htmlFor="stellplatzTyp" className="text-xs font-medium text-gray-600">TG / Stellplatz:</label>
              <select
                id="stellplatzTyp"
                value={currentInputs.stellplatzTyp}
                onChange={handleInputChange}
                className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              >
                {STELLPLATZ_OPTIONS.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
            
            <div className="compact-input-row">
              <label htmlFor="inseratpreis" className="text-xs font-medium text-gray-600">Inseratpreis (€):</label>
              <input
                type="number"
                id="inseratpreis"
                value={currentInputs.inseratpreis}
                onChange={handleInputChange}
                className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div className="compact-input-row">
              <label htmlFor="angebotspreis" className="text-xs font-medium text-gray-600">Angebotspreis (€):</label>
              <input
                type="number"
                id="angebotspreis"
                value={currentInputs.angebotspreis}
                onChange={handleInputChange}
                className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div className="compact-input-row">
              <label htmlFor="gebaudeanteil" className="text-xs font-medium text-gray-600">Gebäude-Anteil (%):</label>
              <input
                type="number"
                id="gebaudeanteil"
                value={currentInputs.gebaudeanteil}
                onChange={handleInputChange}
                className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div className="compact-input-row">
              <label htmlFor="afaSatz" className="text-xs font-medium text-gray-600">AfA-Satz (%):</label>
              <input
                type="number"
                step="0.01"
                id="afaSatz"
                value={currentInputs.afaSatz}
                onChange={handleInputChange}
                className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div className="compact-input-row">
              <label htmlFor="steuersatz" className="text-xs font-medium text-gray-600">Steuersatz (%):</label>
              <input
                type="number"
                step="0.01"
                id="steuersatz"
                value={currentInputs.steuersatz}
                onChange={handleInputChange}
                className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        {/* Vermietung */}
        <div className="p-3 bg-white rounded-lg shadow-sm border border-gray-100">
          <h4 className="font-semibold text-gray-800 mb-3">Vermietung</h4>
          <div className="space-y-1">
            <div className="compact-input-row">
              <label htmlFor="hausgeldUmlagefaehig" className="text-xs font-medium text-gray-600">Hausgeld Umlagef. (€/Monat):</label>
              <input
                type="number"
                id="hausgeldUmlagefaehig"
                value={currentInputs.hausgeldUmlagefaehig}
                onChange={handleInputChange}
                className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div className="compact-input-row">
              <label htmlFor="hausgeldNichtUmlagefaehig" className="text-xs font-medium text-gray-600">Hausgeld nicht Umlagef. (€/Monat):</label>
              <input
                type="number"
                id="hausgeldNichtUmlagefaehig"
                value={currentInputs.hausgeldNichtUmlagefaehig}
                onChange={handleInputChange}
                className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div className="compact-input-row">
              <label htmlFor="hausgeldTotal" className="text-xs font-medium text-gray-600">Hausgeld (Gesamt €/Monat):</label>
              <input
                type="text"
                id="hausgeldTotal"
                value={formatValue(currentInputs.hausgeldUmlagefaehig + currentInputs.hausgeldNichtUmlagefaehig, '€')}
                className="px-2 py-1 text-xs border border-gray-300 rounded-md"
                readOnly
                disabled
              />
            </div>
            
            <div className="compact-input-row">
              <label htmlFor="istKaltmieteProQm" className="text-xs font-medium text-gray-600">IST-Miete (€/m²):</label>
              <input
                type="number"
                step="0.01"
                id="istKaltmieteProQm"
                value={currentInputs.istKaltmieteProQm}
                onChange={handleInputChange}
                className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div className="compact-input-row">
              <label htmlFor="istStellplatzMiete" className="text-xs font-medium text-gray-600">IST TG / Stellplatz (€/Monat):</label>
              <input
                type="number"
                step="1"
                id="istStellplatzMiete"
                value={currentInputs.istStellplatzMiete}
                onChange={handleInputChange}
                className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div className="compact-input-row">
              <label htmlFor="kaltmieteProQm" className="text-xs font-medium text-gray-600">SOLL-Miete (€/m²):</label>
              <input
                type="number"
                step="0.01"
                id="kaltmieteProQm"
                value={currentInputs.kaltmieteProQm}
                onChange={handleInputChange}
                className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div className="compact-input-row">
              <label htmlFor="stellplatzMiete" className="text-xs font-medium text-gray-600">SOLL TG / Stellplatz (€/Monat):</label>
              <input
                type="number"
                step="1"
                id="stellplatzMiete"
                value={currentInputs.stellplatzMiete}
                onChange={handleInputChange}
                className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                disabled={currentInputs.stellplatzTyp === 'Nein'}
              />
            </div>
          </div>
        </div>

        {/* Finanzierung */}
        <div className="p-3 bg-white rounded-lg shadow-sm border border-gray-100">
          <h4 className="font-semibold text-gray-800 mb-3">Finanzierung</h4>
          <div className="space-y-1">
            <div className="compact-input-row">
              <label htmlFor="finanzierungsanteil" className="text-xs font-medium text-gray-600">Finanzierung (%):</label>
              <input
                type="number"
                id="finanzierungsanteil"
                value={currentInputs.finanzierungsanteil}
                onChange={handleInputChange}
                className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div className="compact-input-row">
              <label htmlFor="zinssatz" className="text-xs font-medium text-gray-600">Zinssatz (%):</label>
              <input
                type="number"
                step="0.01"
                id="zinssatz"
                value={currentInputs.zinssatz}
                onChange={handleInputChange}
                className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div className="compact-input-row">
              <label htmlFor="tilgung" className="text-xs font-medium text-gray-600">Tilgung (%):</label>
              <input
                type="number"
                step="0.01"
                id="tilgung"
                value={currentInputs.tilgung}
                onChange={handleInputChange}
                className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div className="compact-input-row">
              <label htmlFor="zinssatzJ10" className="text-xs font-medium text-gray-600">Zinsatz J10 (%):</label>
              <input
                type="number"
                step="0.01"
                id="zinssatzJ10"
                value={currentInputs.zinssatzJ10}
                onChange={handleInputChange}
                className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div className="compact-input-row">
              <label htmlFor="makler" className="text-xs font-medium text-gray-600">Makler (%):</label>
              <input
                type="number"
                step="0.01"
                id="makler"
                value={currentInputs.makler}
                onChange={handleInputChange}
                className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div className="compact-input-row">
              <label htmlFor="notar" className="text-xs font-medium text-gray-600">Notar (%):</label>
              <input
                type="number"
                step="0.01"
                id="notar"
                value={currentInputs.notar}
                onChange={handleInputChange}
                className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div className="compact-input-row">
              <label htmlFor="grundbuchamt" className="text-xs font-medium text-gray-600">Grundbuchamt (%):</label>
              <input
                type="number"
                step="0.01"
                id="grundbuchamt"
                value={currentInputs.grundbuchamt}
                onChange={handleInputChange}
                className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div className="compact-input-row">
              <label htmlFor="grunderwerbst" className="text-xs font-medium text-gray-600">Grunderwerbst. (%):</label>
              <input
                type="number"
                step="0.01"
                id="grunderwerbst"
                value={currentInputs.grunderwerbst}
                onChange={handleInputChange}
                className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div className="compact-input-row">
              <label htmlFor="nebenkostenTotal" className="text-xs font-medium text-gray-600">Nebenkosten (Gesamt %):</label>
              <input
                type="text"
                id="nebenkostenTotal"
                value={formatValue(
                  currentInputs.makler + currentInputs.notar + currentInputs.grundbuchamt + currentInputs.grunderwerbst,
                  '%'
                )}
                className="px-2 py-1 text-xs border border-gray-300 rounded-md"
                readOnly
                disabled
              />
            </div>
          </div>
        </div>

        {/* Entwicklung */}
        <div className="p-3 bg-white rounded-lg shadow-sm border border-gray-100">
          <h4 className="font-semibold text-gray-800 mb-3">Entwicklung</h4>
          <div className="space-y-1">
            <div className="compact-input-row">
              <label htmlFor="mietentwicklungProJahr" className="text-xs font-medium text-gray-600">Mietentwicklung %/Jahr:</label>
              <input
                type="number"
                step="0.01"
                id="mietentwicklungProJahr"
                value={currentInputs.mietentwicklungProJahr}
                onChange={handleInputChange}
                className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div className="compact-input-row">
              <label htmlFor="inflationProJahr" className="text-xs font-medium text-gray-600">Inflation %/Jahr:</label>
              <input
                type="number"
                step="0.01"
                id="inflationProJahr"
                value={currentInputs.inflationProJahr}
                onChange={handleInputChange}
                className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div className="compact-input-row">
              <label htmlFor="wertzuwachsProJahr" className="text-xs font-medium text-gray-600">Wertzuwachs %/Jahr:</label>
              <input
                type="number"
                step="0.01"
                id="wertzuwachsProJahr"
                value={currentInputs.wertzuwachsProJahr}
                onChange={handleInputChange}
                className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div className="compact-input-row">
              <label htmlFor="instandhaltung" className="text-xs font-medium text-gray-600">Instandhaltung (€/m²):</label>
              <input
                type="number"
                id="instandhaltung"
                value={currentInputs.instandhaltung}
                onChange={handleInputChange}
                className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div className="compact-input-row">
              <label htmlFor="instandhaltungJ10" className="text-xs font-medium text-gray-600">Instandh. €/m² (ab J11):</label>
              <input
                type="number"
                id="instandhaltungJ10"
                value={currentInputs.instandhaltungJ10}
                onChange={handleInputChange}
                className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div className="compact-input-row">
              <label htmlFor="mietausfallrisiko" className="text-xs font-medium text-gray-600">Mietausfall (%):</label>
              <input
                type="number"
                step="0.01"
                id="mietausfallrisiko"
                value={currentInputs.mietausfallrisiko}
                onChange={handleInputChange}
                className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div className="compact-input-row">
              <label htmlFor="anfangsinvestitionen" className="text-xs font-medium text-gray-600">Anfängl. Invest. (€):</label>
              <input
                type="number"
                id="anfangsinvestitionen"
                value={currentInputs.anfangsinvestitionen}
                onChange={handleInputChange}
                className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div className="compact-input-row">
              <label htmlFor="anfangsinvest-prozent-display" className="text-xs font-medium text-gray-600">... in % vom Gebäudewert:</label>
              <input
                type="text"
                id="anfangsinvest-prozent-display"
                value={formatValue(
                  currentInputs.angebotspreis > 0
                    ? (currentInputs.anfangsinvestitionen / (currentInputs.angebotspreis * currentInputs.gebaudeanteil / 100)) * 100
                    : 0,
                  '%'
                )}
                className="px-2 py-1 text-xs border border-gray-300 rounded-md transition-colors duration-300"
                readOnly
                disabled
              />
            </div>
            
            <div className="compact-input-row">
              <label htmlFor="sofortabschreibungAktivieren" className="text-xs font-medium text-gray-600">Investition sofort absetzen?</label>
              <div className="flex items-center justify-end w-1/2">
                <input
                  type="checkbox"
                  id="sofortabschreibungAktivieren"
                  checked={currentInputs.sofortabschreibungAktivieren}
                  onChange={handleInputChange}
                  className="form-checkbox h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                />
              </div>
            </div>
            
            <div className="compact-input-row">
              <label htmlFor="investitionenJahr10" className="text-xs font-medium text-gray-600">Invest. J10 (€):</label>
              <input
                type="number"
                id="investitionenJahr10"
                value={currentInputs.investitionenJahr10}
                onChange={handleInputChange}
                className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            
            <div className="compact-input-row">
              <label htmlFor="schwierigkeit" className="text-xs font-medium text-gray-600">Schwierigkeit:</label>
              <select
                id="schwierigkeit"
                value={currentInputs.schwierigkeit}
                onChange={handleInputChange}
                className={`px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-colors duration-300 ${
                  currentInputs.schwierigkeit <= 2 ? 'bg-green-100' : 
                  currentInputs.schwierigkeit <= 3 ? 'bg-yellow-100' : 
                  'bg-red-100'
                }`}
              >
                {SCHWIERIGKEIT_OPTIONS.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Comments Section */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="p-3 bg-white rounded-lg shadow-sm border border-gray-100">
          <label htmlFor="positiveMerkmale" className="block text-sm font-medium text-gray-700 mb-1">
            Positive Merkmale (Kommentar):
          </label>
          <textarea
            id="positiveMerkmale"
            rows={4}
            value={currentInputs.positiveMerkmale}
            onChange={handleInputChange}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
            placeholder="z.B. gute Lage, neuwertiger Zustand..."
          />
        </div>
        
        <div className="p-3 bg-white rounded-lg shadow-sm border border-gray-100">
          <label htmlFor="negativeMerkmale" className="block text-sm font-medium text-gray-700 mb-1">
            Negative Merkmale (Kommentar):
          </label>
          <textarea
            id="negativeMerkmale"
            rows={4}
            value={currentInputs.negativeMerkmale}
            onChange={handleInputChange}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-transparent"
            placeholder="z.B. Renovierungsbedarf, laute Straße..."
          />
        </div>
      </div>
    </div>
  );
};

export default InputSection;